import { Box, Button, Dialog, DialogContent, DialogTitle, MenuItem, Slide, Stack, TextField } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { DatePicker } from "@mui/x-date-pickers";
import { useSnackbar } from "notistack";
import React, { ReactElement } from "react";
import { ApiError } from "../../Api/ApiError";
import { AddStore, EditStoreSetting, getStoreData } from "../../Api/Store";
import { Time } from "../../Helper/Time";
import { StoreData } from "../../Objects/StoreInterface";
import LabelItem from "../atoms/LabelItem";


const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />
})

export interface StoreSettingEditDialogProps {
    storeId?: number
    onClose: (isEdit: boolean) => void
}

const StoreSettingEditDialog: React.FC<StoreSettingEditDialogProps> = ({
    storeId,
    onClose,
}) => {
    let { enqueueSnackbar } = useSnackbar()

    const [open, setOpen] = React.useState<boolean>(false)
    const [storeData, setStoreData] = React.useState<StoreData>()
    const [strPassConfirm, setStrPassConfirm] = React.useState<string>("")

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const doEdit = React.useCallback(() => {
        if( storeData ) {
            if( storeId === 0 ) {
                AddStore({
                    store_data: storeData
                }, {signal: abortController.signal}).then( () => {
                    enqueueSnackbar("データを追加しました", { variant: "success" })
                    onClose(true)
                }).catch((e: any) => {
                    if( e.__CANCEL__ ) {
                        console.info("canceled")
                    } else if (e instanceof ApiError) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    } else if (e instanceof Error) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    }else {
                        enqueueSnackbar("通信に失敗しました", { variant: "error" })
                    }
                })
                return;
            }
            EditStoreSetting({
                store_data: storeData
            }, {signal: abortController.signal}).then( ({status}) => {
                enqueueSnackbar("データを変更しました", { variant: "success" })
                onClose(true)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, storeData, enqueueSnackbar, onClose, storeId])

    /**
     * 店舗データ初期化
     */
    React.useEffect(() => {
        const controller = new AbortController()
        setOpen(false)
        if( storeId===0 ) {
            setStoreData({})
            setOpen(true)
        } else if( storeId ) {
            getStoreData(storeId, {signal: controller.signal}).then((storeData: StoreData) => {
                setStoreData(storeData)
                setOpen(true)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
        return () => {
            controller.abort()
        }
    },[storeId, enqueueSnackbar])
    
    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            onClose={() => {
                onClose(false)
            }}
            fullWidth
        >
            <DialogTitle>{storeId===0 ? "店舗を追加" : "店舗情報を編集"}</DialogTitle>
            <DialogContent>
                <Box component="form" onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                    e.preventDefault()
                    doEdit()
                }} noValidate>
                    <Stack
                        sx={{
                            mb: 2
                        }}
                        spacing={2}
                    >
                        <LabelItem
                            title="ストア名"
                        >
                            <TextField
                                variant="standard"
                                value={storeData?.str_name ?? ""}
                                fullWidth
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    if( storeData ) {
                                        let data: StoreData = {
                                            ...storeData,
                                            str_name: e.target.value ? e.target.value : ""
                                        }
                                        setStoreData(data)
                                    }
                                }}
                            />
                        </LabelItem>
                        <LabelItem
                            title="ログイン名"
                        >
                            <TextField
                                variant="standard"
                                value={storeData?.str_login_str ?? ""}
                                fullWidth
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    if( storeData ) {
                                        let data: StoreData = {
                                            ...storeData,
                                            str_login_str: e.target.value ? e.target.value : ""
                                        }
                                        setStoreData(data)
                                    }
                                }}
                            />
                        </LabelItem>
                        {storeId===0 &&
                            <>
                                <LabelItem
                                    title="ログインパスワード"
                                >
                                    <TextField
                                        type="password"
                                        variant="standard"
                                        value={storeData?.str_pass ?? ""}
                                        fullWidth
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            if( storeData ) {
                                                let data: StoreData = {
                                                    ...storeData,
                                                    str_pass: e.target.value ? e.target.value : ""
                                                }
                                                setStoreData(data)
                                            }
                                        }}
                                    />
                                </LabelItem>
                                <LabelItem
                                    title="ログインパスワード(確認)"
                                >
                                    <TextField
                                        type="password"
                                        variant="standard"
                                        value={strPassConfirm}
                                        fullWidth
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            setStrPassConfirm(e.target.value)
                                        }}
                                    />
                                </LabelItem>
                                <LabelItem
                                    title="1コマの時間数"
                                >
                                    <TextField
                                        variant="standard"
                                        value={storeData?.str_unit ? storeData.str_unit.toStringHM() : ""}
                                        fullWidth
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            setStoreData(prev => {
                                                return {
                                                    ...prev,
                                                    str_unit: e.target.value ? new Time(e.target.value) : undefined
                                                }
                                            })
                                        }}
                                        select
                                    >
                                        <MenuItem value="0:30">30分</MenuItem>
                                        <MenuItem value="1:00">60分</MenuItem>
                                    </TextField>
                                </LabelItem>
                                <LabelItem
                                    title="営業開始日"
                                >
                                    <DatePicker
                                        onChange={(newValue: Date|null) => {
                                            setStoreData(prev => {
                                                return {
                                                    ...prev,
                                                    str_open_date: newValue ? newValue : undefined
                                                }
                                            })
                                        }}
                                        value={storeData?.str_open_date}
                                        renderInput={params => <TextField {...params} fullWidth variant="standard"/>}
                                        mask="____/__/__"
                                    />
                                </LabelItem>
                            </>
                        }
                        <LabelItem
                            title="営業終了日"
                        >
                            <DatePicker
                                onChange={(newValue: Date|null) => {
                                    if( storeData ) {
                                        let data: StoreData = {
                                            ...storeData,
                                            str_close_date: newValue ? newValue : undefined
                                        }
                                        setStoreData(data)
                                    }
                                }}
                                value={storeData?.str_close_date}
                                renderInput={params => <TextField {...params} fullWidth variant="standard"/>}
                                mask="____/__/__"
                            />
                        </LabelItem>
                    </Stack>
                    <Stack
                        direction="row"
                        justifyContent="flex-end"
                    >
                        <Button
                            variant="outlined"
                            type="submit"
                        >{storeId===0 ? "追加を実行" : "変更を実行"}</Button>
                    </Stack>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default StoreSettingEditDialog